<template>
  <div
    class="auth-input text-left"
    :class="{
      'has-error': error.length,
      'is-loading': loading,
      'is-disabled': disabled,
      'is-readonly': readonly
    }"
  >
    <label :for="'auth_input_' + _uid">{{ label }}</label>
    <!-- if not file input type -->
    <input
      v-if="!locked && inputType !== 'file'"
      :id="id || 'auth_input_' + _uid"
      :type="inputType"
      :value="value"
      class="block w-full outline-none"
      :placeholder="placeholder"
      :readonly="loading || locked || readonly"
      :disabled="loading || locked || disabled"
      :required="required"
      :autocomplete="autocomplete"
      :maxlength="maxlength"
      :accept="$attrs.accept"
      @input="$emit('input', $event.target.value)"
      @blur="$emit('blur', $event.target.value)"
    />

    <!-- if file input type -->
    <input
      v-else-if="!locked && inputType === 'file'"
      :id="id || 'auth_input_' + _uid"
      type="file"
      class="block w-full outline-none"
      :multiple="multiple"
      :accept="$attrs.accept"
      :readonly="loading"
      :disabled="loading || disabled"
      :required="required"
      @blur="$emit('blur', $event.target.value)"
      @change="onChange"
    />

    <div
      v-else
      class="flex items-center justify-between"
    >
      <span class="text-black">{{ value }}&nbsp;</span>
      <icon
        icon="lock"
        class="icons-grey w-4 h-4"
      />
    </div>
    <slot />
    <p
      v-if="error.length"
      class="text-left text-xs mb-0 mt-2 italic"
      v-html="$sanitizeHtml(error.join(', '))"
    />
  </div>
</template>

<script>
export default {
  name: "AuthInput",
  props: {
    id: {
      type: String,
      default: null
    },
    value: {
      type: [String, Number],
      required: false,
      default: (type) => {
        return type === Number ? 0 : "";
      }
    },
    multiple: {
      type: Boolean,
      default: false
    },
    error: {
      type: Array,
      default: function() {
        return [];
      }
    },
    label: {
      type: String,
      default: ""
    },
    placeholder: {
      type: String,
      default: ""
    },
    locked: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    maxlength: {
      type: Number,
      default: 255
    },
    autocomplete: {
      type: [Boolean,String],
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    inputType: {
      required: true,
      default: "text",
      validator: function(value) {
        return (
          ["text", "email", "url",  "search", "password", "tel", "file"].indexOf(value) !== -1
        );
      }
    }
  },
  methods: {
    onChange(event) {
      if (this.inputType !== "file") return;

      if (!this.multiple) {
        return;
      }

      const files = Array.from(event.target.files);
      this.$emit("input", this.multiple ? files : files[0]);
    }
  }
};
</script>

<style>
.auth-input {
  @apply border-grey-200 border p-3 relative mt-6 mb-4 text-grey-700;
  transition: all 0.2s ease-in-out;
}

.auth-input:focus-within {
  @apply text-secondary-500 border-secondary-500;
}

.auth-input.has-error {
  @apply text-negative-600 border-negative-600;
}

.auth-input.is-disabled {
  @apply text-grey-500 border-grey-300;
}

.auth-input label {
  @apply absolute pin-t pin-l text-xs tracking-wide uppercase font-bold -mt-2 bg-white ml-1 px-2 cursor-pointer;
}

.auth-input input {
  @apply bg-transparent;
}

.auth-input input::placeholder {
  @apply italic;
}
</style>
